import { CHANGE_REQUEST_NOTE_MAX, type PortalChangeRequestDto, type PortalWorkDto } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconCalendarRepeat } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText } from '../../api';
import { dayLong, dayRange, formatDateTime } from '../../format';

/** Stan ostatniej prośby o zmianę terminu (pod pracą w harmonogramie strefy). */
function RequestState({ r }: { r: PortalChangeRequestDto }) {
  if (r.status === 'otwarta')
    return (
      <Alert color="blue" p="xs" mt={6}>
        <Text size="sm" fw={600}>
          Prośba o zmianę terminu wysłana {formatDateTime(r.createdAt)}
        </Text>
        <Text size="sm">„{r.note}”</Text>
        <Text size="xs" c="dimmed">
          Sprawdzamy wolne terminy — o nowym terminie poinformujemy e-mailem.
        </Text>
      </Alert>
    );
  if (r.status === 'odpowiedz')
    return (
      <Alert color="orange" p="xs" mt={6}>
        <Text size="sm" fw={600}>
          Odpowiedź na prośbę o zmianę terminu{r.resolvedAt ? ` (${formatDateTime(r.resolvedAt)})` : ''}
        </Text>
        <Text size="sm">{r.answer}</Text>
        <Text size="xs" c="dimmed">
          Termin pozostał bez zmian.
        </Text>
      </Alert>
    );
  return (
    <Text size="xs" c="green.7" mt={6}>
      Termin zmieniony na Twoją prośbę{r.resolvedAt ? ` ${formatDateTime(r.resolvedAt)}` : ''}.
    </Text>
  );
}

/**
 * „Poproś o zmianę terminu” przy zaplanowanej pracy (docs/18): klient pisze, na kiedy chciałby termin; koordynator
 * przesuwa pracę (e-mail z nowym terminem) albo odpowiada. W podglądzie biura (`base` brak) — przycisk nieaktywny.
 */
export function ChangeRequest({ work, base }: { work: PortalWorkDto; base?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const send = useMutation({
    mutationFn: () => api<PortalChangeRequestDto>(`${base}/zmiana-terminu`, { method: 'POST', body: { taskId: work.taskId, note } }),
    onSuccess: async () => {
      setOpen(false);
      setNote('');
      await qc.invalidateQueries({ queryKey: ['public-portal'] });
    },
    onError: (e) => setError(errorText(e)),
  });
  const r = work.changeRequest;

  return (
    <>
      {r ? <RequestState r={r} /> : null}
      {work.canRequestChange && (
        <Group mt={6}>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconCalendarRepeat size={16} />}
            disabled={!base}
            title={base ? undefined : 'W podglądzie biura prośba jest wyłączona'}
            onClick={() => {
              setError('');
              setOpen(true);
            }}
          >
            Poproś o zmianę terminu
          </Button>
        </Group>
      )}
      <Modal opened={open} onClose={() => setOpen(false)} title="Prośba o zmianę terminu">
        <Stack>
          <Text size="sm">
            <b>{work.title}</b>
            <br />
            Obecny termin: {work.dateFrom === work.dateTo ? dayLong(work.dateFrom) : dayRange(work.dateFrom, work.dateTo)}
          </Text>
          <Textarea
            label="Na kiedy chcesz przesunąć termin?"
            placeholder="np. wypadł mi wyjazd — proszę o przesunięcie o tydzień"
            autosize
            minRows={3}
            maxLength={CHANGE_REQUEST_NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            data-autofocus
          />
          <Text size="xs" c="dimmed">
            Sprawdzimy wolne terminy ekip. O nowym terminie poinformujemy e-mailem; do tego czasu obowiązuje obecny termin.
          </Text>
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button loading={send.isPending} disabled={note.trim().length < 3} onClick={() => send.mutate()}>
              Wyślij prośbę
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
